import { isToday, isTomorrow, isYesterday } from 'date-fns';
import { upperFirst } from 'es-toolkit/compat';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { useShopContext } from '@frontend/utils';

export interface LocaleContextType {
  /**
   * The key can be used to detect locale changes in react hook dependencies.
   * It is a convenience property to simplify locale change detection.
   *
   * @example
   * const { key } = useLocale();
   *
   * useEffect(() => {
   *  console.log('Locale changed');
   * }, [key]);
   */
  key: string;
  locale: Intl.Locale;
  language: string;
  formatters: {
    relativeDay: Intl.RelativeTimeFormat;
    datetime: Intl.DateTimeFormat;
    date: Intl.DateTimeFormat;
    time: Intl.DateTimeFormat;
    relativeDatetime: (date: Date) => string;
    relativeDate: (date: Date) => string;
    currency: (value: number, hideZeroDecimals?: boolean) => string;
  };
}

const LocaleContext = createContext<LocaleContextType>(null!);

export interface LocaleContextProviderProps {
  locale: Intl.Locale;
  language: string;
}

export const LocaleProvider = (
  props: PropsWithChildren<LocaleContextProviderProps>,
) => {
  const { shop } = useShopContext();

  const value: LocaleContextType = useMemo(() => {
    const currencyFormatter = new Intl.NumberFormat(props.locale, {
      style: 'currency',
      currency: shop.currency,
    });

    const currencyNoDecimalsFormatter = new Intl.NumberFormat(props.locale, {
      style: 'currency',
      currency: shop.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    const formatters = {
      relativeDay: new Intl.RelativeTimeFormat(props.locale, {
        numeric: 'auto',
        style: 'short',
      }),
      datetime: new Intl.DateTimeFormat(props.locale, {
        dateStyle: 'short',
        timeStyle: 'short',
      }),
      date: new Intl.DateTimeFormat(props.locale, {
        dateStyle: 'short',
      }),
      time: new Intl.DateTimeFormat(props.locale, {
        timeStyle: 'short',
      }),
      relativeDatetime: (date: Date) => {
        if (isToday(date)) {
          return formatters.time.format(date);
        } else if (isYesterday(date)) {
          return `${upperFirst(formatters.relativeDay.format(-1, 'day'))} ${formatters.time.format(date)}`;
        }

        return formatters.datetime.format(date);
      },
      relativeDate: (date: Date) => {
        if (isToday(date)) {
          return `${upperFirst(formatters.relativeDay.format(0, 'day'))} (${date.toLocaleDateString(props.locale, { weekday: 'long' })})`;
        } else if (isTomorrow(date)) {
          return `${upperFirst(formatters.relativeDay.format(+1, 'day'))} (${date.toLocaleDateString(props.locale, { weekday: 'long' })})`;
        }

        return formatters.date.format(date);
      },
      currency: (value: number, hideZeroDecimals = false) => {
        const normalized = (value + Number.EPSILON) / 100;

        if (hideZeroDecimals && normalized % 1 === 0) {
          return currencyNoDecimalsFormatter.format(normalized);
        }

        return currencyFormatter.format(normalized);
      },
    };

    return {
      key: props.locale + props.language,
      locale: props.locale,
      language: props.language,
      formatters,
    };
  }, [props.language, props.locale, shop.currency]);

  return (
    <LocaleContext.Provider value={value}>
      {props.children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  return useContext(LocaleContext);
};
